import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Pagination } from '@material-ui/lab';
import { 
    makeStyles,
    createStyles,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    FormGroup,
    Grid,
    Theme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { OrderCard } from '../../components/OrderCard';
import { OrderStatusFilter } from '../../components/ListOrders/OrderStatusFilter';
import AlertComponent from '../../components/Alert/AlertComponent';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useLoadMoreOrders } from '../../hooks/useLoadMoreOrders';
import { getOrdersByClient } from '../../flux/actions/order-actions';
import { OrderModel } from '../../models/order-model';
import UserModel from '../../models/user-model';

import listOrdersStyles from './styles';

const allOrderStatus = {
    name: '',
    label: 'Todos'
};

const orderStatus = [
    {
        name: 'RECIBIDO',
        label: 'Pendiente'
    },
    {
        name: 'ARMADO',
        label: 'En proceso'
    },
    {
        name: 'LISTO',
        label: 'Listo para entrega'
    },
    {
        name: 'EN_ENTREGA',
        label: 'En tránsito'
    },
    {
        name: 'TERMINADO',
        label: 'Completado'
    },
    {
        name: 'CANCELADO', // Incluye rechazado y Retenido
        label: 'Cancelado'
    }
];

const useStyles = makeStyles((theme: Theme) => 
    createStyles ({
        loadMoreButton: {
            backgroundColor: '#D32F3F' ,
            color: 'white',
            width: '100%',
            marginBottom: '20px',
            '&:hover': {
                backgroundColor: '#D32F3F' ,
                color: 'white'
            }
        },
        circularProgress: {
            display: 'flex', 
            justifyContent: 'center', 
            margin: '10px 0px 10px 0px'
        },
        alertContainer: {
            margin: '0px 150px',
            [theme.breakpoints.down('xs')]: {
                margin: '0px 0px 80px 0px'
            }
        },
        lastCardContainer: {
            marginBottom: '15px'
        },
        orderStatusModal: {
            '& .MuiDialog-paper': {
                width: '400px'
            },
            '& .Mui-checked': {
                color: '#D32F2F' 
            }
        }
    })
);

export const OrdersList = () => {
    const dispatch = useDispatch();
    const { width } = useWindowDimensions();
    const [ordersMobile, loadMore, loadingOrders, fetchData, noMoreOrders, mobileError] = useLoadMoreOrders(width);
    const classes = listOrdersStyles();
    const listOrderStyles = useStyles();
    const [page, setPage] = useState(1);
    const [fromPagination, setFromPagination] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [filterUpdated, setFilterUpdated] = useState(false);
    const [open, setOpen] = useState(false);

    const { ordersData, errorStatus } = useSelector((state: any) => state.order);
    const user: UserModel = useSelector((state: any) => state.authReducer.user)
    const { orders, pages } = ordersData; 

    useEffect(() => {
        if (!fromPagination && width > 500) {
            setPage(1);
            setFromPagination(false);
            dispatch(getOrdersByClient(page, 9, statusFilter));
        } else if (width < 500 && filterUpdated) {
            fetchData(1, statusFilter, true);
        }
    }, [statusFilter, page, filterUpdated]);

    useEffect(() => {
        if (fromPagination) {
            setFromPagination(false);
            dispatch(getOrdersByClient(page, 9, statusFilter));
        }
    }, [fromPagination, page]);
    
    const handleNextPage = (event: any, page: number) => {
        setPage(page);
        setFromPagination(true);
    }

    const onCheckStatus = (_name: string, checked: boolean) => {
        if (checked) {
            setStatusFilter([
                ...statusFilter,
                _name 
            ]);
            setFilterUpdated(true);
        } else {
            const filtered = statusFilter.filter((name: string) => name !== _name);
            setStatusFilter(filtered);
            setFilterUpdated(true);
        }
    }

    const onCheckAllOrders = (_name: string, checked: boolean) => {
        if (checked) {
            setStatusFilter([]);
        }
    }

    return (
        <Container>
            <Grid
                alignItems="center" 
                direction="row" 
                justify="flex-start" 
                spacing={3} 
                container
                style={{ overflowAnchor: 'none' }}
            >
                <Grid xs={12} item>
                    <h1 className={classes.title}>Lista de pedidos</h1>
                </Grid>
                { user.id && 
                    (width > 500 ? errorStatus !== 401 : mobileError === false) ?
                    <>
                        <Grid xs={12} item style={{display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1rem'}}>
                            { width > 500 
                            ?   <>
                                    <OrderStatusFilter key={0} checked={statusFilter.length === 0} onCheckStatus={onCheckAllOrders} orderStatus={allOrderStatus}/>
                                    {orderStatus.map((status: any) => (
                                        <OrderStatusFilter 
                                            key={status.name} 
                                            checked={statusFilter.includes(status.name)} 
                                            orderStatus={status} 
                                            onCheckStatus={onCheckStatus} 
                                        />
                                    ))}
                                </>
                            :   
                                <>
                                    <Button 
                                        variant="outlined"
                                        onClick={() => setOpen(true)}
                                        color="primary"
                                        >
                                        PEDIDOS
                                    </Button>
                                    <Dialog
                                        className={listOrderStyles.orderStatusModal}
                                        open={open}
                                        onClose={() => setOpen(false)}
                                        >
                                        <DialogTitle>
                                            Pedidos
                                        </DialogTitle>
                                        <DialogContent>
                                            <FormGroup>
                                                <FormControlLabel
                                                    control={<Checkbox checked={statusFilter.length === 0} onChange={(ev, ckd) => onCheckAllOrders('', ckd)}/>}
                                                    label={allOrderStatus.label}
                                                />
                                                {orderStatus.map(
                                                    (status, idx) => 
                                                        <FormControlLabel
                                                            key={idx}
                                                            control={<Checkbox checked={statusFilter.includes(status.name)} onChange={(ev, ckd) => onCheckStatus(status.name, ckd)} />}
                                                            label={status.label}
                                                        />
                                                )}
                                            </FormGroup>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={() => setOpen(false)} color="primary">
                                                Aceptar
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </>
                            }
                        </Grid>
                        { (orders.length === 0 && ordersMobile.length === 0) ? 
                                <Grid item xs={12} className={listOrderStyles.alertContainer}>
                                    <AlertComponent severity="info">No tienes pedidos que cumplan con el filtro.</AlertComponent>
                                </Grid>
                            :
                            width > 500 
                            ? orders?.map((order: OrderModel) => (
                                <Grid xs={12} sm={4} item key={order._id}>
                                    <OrderCard {...order} />
                                </Grid>))
                            : ordersMobile.map((order: OrderModel, i: number) => (
                                <Grid xs={12} sm={4} item key={order._id} className={(ordersMobile.length - 1) === i ? listOrderStyles.lastCardContainer : ''}>
                                    <OrderCard {...order} />
                                </Grid>
                            ))
                        }
                        { (width < 500 && loadingOrders) && 
                            <Grid xs={12} item className={listOrderStyles.circularProgress}>
                                <CircularProgress color="secondary" />
                            </Grid>  
                        }
                        {
                            width > 500 ? 
                                <Grid xs={12} item>
                                    <Pagination
                                        className={classes.pagination} 
                                        color="secondary"
                                        defaultPage={page}
                                        count={pages}
                                        page={page}
                                        onChange={handleNextPage}
                                    />
                                </Grid>
                            : (
                                !noMoreOrders &&
                                <Grid xs={12} item>
                                    <Button className={listOrderStyles.loadMoreButton} variant="contained" type="button" onClick={() => loadMore(statusFilter)}>
                                        Cargar más pedidos...
                                    </Button>
                                </Grid>
                            )
                        }
                    </>
                    :
                    <Grid item xs={12} className={listOrderStyles.alertContainer}>
                        <AlertComponent severity="info">Por favor <Link to="/login">inicia sesión</Link> para ver tus pedidos.</AlertComponent>
                    </Grid>
                }
            </Grid>
        </Container>
    );
}
